import { Box, Divider, Grid } from "@mui/material";
import ControlledTextField from "../../Controller/ControlledTextField";
import ControlledSelect from "../../Controller/ControlledSelect";
import { useTranslation } from "react-i18next";
import { IContact } from "../../../types/Contact/contact";
import { useFormContext, useWatch } from "react-hook-form";
import { useEffect, useState } from "react";
import { useDisable } from "../../../hooks/use-disable";
import ControlledAddressBox from "../../Controller/ControlledAddressBox";
import LabelInput from "../../UI/LabelInput";
import { countryOptions } from "../../../utils/Global";
import { countryFormatter } from "../../../utils/Formatter/Global";
import { IAddress } from "../../../types/global";

type Props = {
  index: number;
  addressList: any[] | null | undefined;
};

const AddressDetail = ({ index, addressList }: Props) => {
  const {
    control,
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext<IContact>();
  const { t } = useTranslation();

  const [addressValue, setAddressValue] = useState<IAddress>({
    address_type: "",
    name: "",
    district: "",
    is_default: true,
    postal_code: "",
    province: "",
    sub_district: "",
    address_contact_name: "",
    address_contact_phone: "",
    address_contact_fax: "",
    country: "",
  });

  const defaultAddressValue = useWatch({
    control,
    name: `address_list.0`,
  });

  const isSameAsDefault = useWatch({
    control,
    name: `address_list.${index}.is_same_as_default_address`,
  });

  useEffect(() => {
    if (isSameAsDefault) {
      const { address_type, ...other } = defaultAddressValue;
      const previousValue = getValues(`address_list.${index}`);
      const newAddressValue = {
        ...other,
        address_type: previousValue.address_type,
        address_contact_name: previousValue.address_contact_name,
        address_contact_phone: previousValue.address_contact_phone,
        is_default: false,
        is_same_as_default_address: true,
      };
      setValue(`address_list.${index}`, newAddressValue);
      setAddressValue(newAddressValue);
    }
  }, [defaultAddressValue, getValues, index, isSameAsDefault, setValue]);

  const [disabled] = useDisable();

  useEffect(() => {
    if (disabled) {
      if (addressList && addressList[index]) {
        setAddressValue(addressList[index]);
      } else {
        setAddressValue({
          address_type: "",
          name: "",
          district: "",
          is_default: true,
          postal_code: "",
          province: "",
          sub_district: "",
          address_contact_name: "",
          address_contact_phone: "",
          address_contact_fax: "",
          country: "",
        });
      }
    }
  }, [addressList, disabled, index]);

  return (
    <>
      <Grid item xs={12}>
        <ControlledTextField
          name={`address_list.${index}.name`}
          control={control}
          label={t("contact.address.index")}
          error={Boolean(errors.address_list?.[index]?.name)}
          helperText={
            errors.address_list &&
            errors.address_list?.[index]?.name &&
            errors.address_list?.[index]?.name?.message
          }
          rows={4}
          multiline
          disabled={isSameAsDefault}
          viewMode={disabled}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
        <ControlledAddressBox
          name={`address_list.${index}`}
          type="sub_district"
          disabled={isSameAsDefault}
          getValues={getValues}
          setValue={setValue}
          addressValue={addressValue}
          setAddressValue={setAddressValue}
          viewMode={disabled}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
        <ControlledAddressBox
          name={`address_list.${index}`}
          type="district"
          disabled={isSameAsDefault}
          getValues={getValues}
          setValue={setValue}
          addressValue={addressValue}
          setAddressValue={setAddressValue}
          viewMode={disabled}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
        <ControlledAddressBox
          name={`address_list.${index}`}
          type="province"
          disabled={isSameAsDefault}
          getValues={getValues}
          setValue={setValue}
          addressValue={addressValue}
          setAddressValue={setAddressValue}
          viewMode={disabled}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
        <ControlledAddressBox
          name={`address_list.${index}`}
          type="postal_code"
          disabled={isSameAsDefault}
          getValues={getValues}
          setValue={setValue}
          addressValue={addressValue}
          setAddressValue={setAddressValue}
          viewMode={disabled}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
        {disabled ? (
          <LabelInput
            label={t("contact.address.country")}
            value={countryFormatter(getValues(`address_list.${index}.country`))}
          />
        ) : (
          <ControlledSelect
            name={`address_list.${index}.country`}
            control={control}
            label={t("contact.address.country")}
            error={Boolean(errors.address_list?.[index]?.country)}
            helperText={
              errors.address_list &&
              errors.address_list?.[index]?.country &&
              errors.address_list?.[index]?.country?.message
            }
            options={countryOptions}
            disabled={true}
          />
        )}
      </Grid>

      {index !== 0 && (
        <>
          <Box width="100%">
            <Divider sx={{ mt: 2 }} variant="middle" />
          </Box>
          <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
            <ControlledTextField
              name={`address_list.${index}.address_contact_name`}
              control={control}
              label={t("contact.address.contact_person_name")}
              error={Boolean(
                errors.address_list?.[index]?.address_contact_name
              )}
              helperText={
                errors.address_list &&
                errors.address_list?.[index]?.address_contact_name &&
                errors.address_list?.[index]?.address_contact_name?.message
              }
              viewMode={disabled}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
            <ControlledTextField
              name={`address_list.${index}.address_contact_phone`}
              control={control}
              label={t("contact.address.phone_number")}
              error={Boolean(
                errors.address_list?.[index]?.address_contact_phone
              )}
              helperText={
                errors.address_list &&
                errors.address_list?.[index]?.address_contact_phone &&
                errors.address_list?.[index]?.address_contact_phone?.message
              }
              viewMode={disabled}
            />
          </Grid>
        </>
      )}
    </>
  );
};

export default AddressDetail;
